// TenantGate.tsx — strict subdomain gate, applied ABOVE auth/routing.
//
// On a production `*.mypharmacyiq.com` host, the app only renders if the subdomain resolves to a
// real tenant in our DB (via `GET /api/identity/tenants/resolve/`). Anything else — `app.`,
// `admin.`, typos, unknown workspaces — gets the onboarding redirect splash and never sees the UI
// or a login. Exempt (render normally): non-production hosts (localhost, *.azurestaticapps.net, the
// raw App Service host, the apex) and `www` — these yield a null subdomain. This is UX/exposure
// control; the JWT + RLS remain the real authorization boundary (and the backend login guard
// independently rejects logins on non-tenant hosts).
import type { ReactNode } from 'react';
import { tenantSubdomainFromHost, useTenantBranding } from '@/auth/tenant';
import WorkspaceRedirect from '@/routes/WorkspaceRedirect';
import { Logo } from '@/foundation/primitives';

export function TenantGate({ children }: { children: ReactNode }) {
  const subdomain = tenantSubdomainFromHost();
  const { data, isLoading } = useTenantBranding();

  // Exempt host (localhost / preview / apex / www) → render normally.
  if (!subdomain) return <>{children}</>;
  // Resolving the workspace — brief splash so we never flash a login on a stray host.
  if (isLoading) return <GateSplash />;
  // Resolved to "no such workspace" (404) → onboarding redirect, no UI/login.
  if (data === null) return <WorkspaceRedirect subdomain={subdomain} />;
  // Real tenant resolved — OR a transient resolve error (fail-open for availability; the backend
  // login guard still rejects a login on a non-tenant host, so this can't expose another tenant).
  return <>{children}</>;
}

function GateSplash() {
  return (
    <div style={page}>
      <div style={ringWrap}>
        <div style={ring} />
        <div style={badge}>
          <Logo size={30} />
        </div>
      </div>
    </div>
  );
}

const page: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg-app)',
};

const ringWrap: React.CSSProperties = { position: 'relative', width: 76, height: 76 };

const ring: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '3px solid var(--primary-lighter)',
  borderTopColor: 'var(--primary-main)',
  animation: 'piqSpin 1s linear infinite',
};

const badge: React.CSSProperties = {
  position: 'absolute',
  inset: 13,
  borderRadius: '50%',
  background: 'var(--bg-card)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'var(--shadow-dropdown)',
};
